import React, { useState } from 'react'
import Layout from '../components/common/Layout'
import { ClientOnly } from '../components/common'
import { MetaData as MetaTags } from '../components/common/meta'
import FirebaseProvider from '../components/idealline/Firebase'
import GoProImport from '../components/idealline/GoProImport'
import Notification from '../components/common/Notification'
import { onImportLine } from '../utils/lineUtils'
import { trackEvent } from '../utils/analytics'

const GoProImporterPage = ({ location }) => {
  const [importError, setImportError] = useState(null)
  const [importWarning, setImportWarning] = useState(null)
  const [triggerOpen, setTriggerOpen] = useState(false)

  const openImportModal = () => {
    setTriggerOpen(true)
    // Reset after a brief moment so it can be triggered again
    setTimeout(() => {
      setTriggerOpen(false)
    }, 100)
  }

  return (
    <Layout>
      <MetaTags
        title="GoPro Video Racing Line Importer - Extract GPS Telemetry from Your Videos"
        description="Turn your GoPro videos into racing lines. Import GPS telemetry from Hero 5+ cameras and analyze your laps in 3D. Works with MP4, MOV, and 360° videos."
        location={location}
      />

      <div>
        {/* Hero Section */}
        <section className="row-full bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-[#5159a2] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              New Feature
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-racing uppercase text-gray-900 mb-6">
              Your GoPro Is Now
              <br />
              <span className="text-[#5159a2]">Your Data Logger</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
              Extract GPS telemetry from your GoPro videos and create stunning 3D racing lines.
              No external GPS tracker needed.
            </p>

            {/* Hero CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <button
                onClick={openImportModal}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#5159a2] text-white rounded-xl font-bold hover:bg-[#424d8a] transition-all shadow-lg hover:shadow-xl cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Import Your First Video
              </button>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:border-gray-400 hover:shadow-md transition-all"
              >
                See How It Works
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </a>
            </div>
          </div>

          {/* Video Demo */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900 aspect-video">
              <video
                className="w-full h-full object-cover"
                controls
                preload="metadata"
                playsInline
              >
                <source src="/videos/GoPro-racing-line-visualizer.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              Watch how easy it is to import your GoPro video and create a 3D racing line
            </p>
          </div>

          {/* CTA Section */}
          <div id="import" className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              Import Your GoPro Video Now
            </h2>
            <p className="text-gray-600 mb-6 text-center">
              Upload your video and we'll extract GPS data automatically
            </p>

            <ClientOnly>
              <FirebaseProvider>
                <div className="flex justify-center">
                  <GoProImport
                    triggerOpen={triggerOpen}
                    onImport={async (points, carInfo, telemetryId) => {
                    console.log("GoPro import callback:", { points, carInfo, telemetryId });
                    setImportError(null);
                    setImportWarning(null);

                    if (!points) {
                      if (carInfo && carInfo.message) {
                        setImportError(carInfo.message);
                      } else {
                        setImportError("Failed to extract GPS data from video.");
                      }
                      return;
                    }

                    try {
                      const result = await onImportLine({
                        points,
                        carInfo,
                        telemetryId
                      });

                      if (result.lineId) {
                        if (result.warning) {
                          setImportWarning(result.warning);
                        }
                        trackEvent("telemetry_import_complete", {
                          product: "gopro",
                          placement: "importer",
                          stage: "line_creation",
                        });
                        window.location.href = result.url;
                      } else {
                        trackEvent("telemetry_import_error", {
                          product: "gopro",
                          placement: "importer",
                          stage: "line_creation",
                          error_code: "line_creation_failed",
                        });
                        setImportError('Failed to create racing line. Please try again.');
                      }
                    } catch (error) {
                      console.error('Import error:', error);
                      trackEvent("telemetry_import_error", {
                        product: "gopro",
                        placement: "importer",
                        stage: "line_creation",
                        error_code: "line_creation_failed",
                      });
                      setImportError(error.message || 'Failed to import. Please try again.');
                    }
                  }} />
                </div>
              </FirebaseProvider>
            </ClientOnly>

            {/* Requirements */}
            <div className="mt-8 bg-blue-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Requirements:</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>GoPro Hero 5 or newer</li>
                <li>GPS enabled in camera settings</li>
                <li>Recorded outdoors with clear sky view</li>
                <li>File formats: .mp4, .mov, or .360</li>
              </ul>
            </div>
          </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="row-full bg-white py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl sm:text-4xl font-racing uppercase text-center mb-4">
              Why Use GoPro Telemetry?
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Simplify your track day setup. One device, perfect data.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#5159a2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">18Hz GPS Built-In</h3>
                <p className="text-gray-600">
                  Higher accuracy than most external GPS loggers. Your GoPro Hero 5+ has professional-grade positioning.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">No Extra Gear</h3>
                <p className="text-gray-600">
                  Ditch the external GPS tracker, phone mounts, and Bluetooth pairing. Just press record and drive.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Video + Data</h3>
                <p className="text-gray-600">
                  GPS telemetry is stored right in your video file. One file, both your footage and racing data.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Local Processing</h3>
                <p className="text-gray-600">
                  Your video is processed in your browser. Only GPS data is uploaded. Your footage stays private.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Auto Lap Detection</h3>
                <p className="text-gray-600">
                  We automatically detect your laps and find the track. Just select your fastest lap and import.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#5159a2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">3D Visualization</h3>
                <p className="text-gray-600">
                  See your racing line in stunning 3D. Compare laps, share with friends, and improve your times.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="row-full py-16 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl sm:text-4xl font-racing uppercase text-center mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12">
              From video to 3D racing line in minutes
            </p>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="relative">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#5159a2] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    1
                  </div>
                  <h3 className="font-bold mb-2">Record Your Session</h3>
                  <p className="text-sm text-gray-600">
                    Start your GoPro recording outside to lock GPS signal before your first lap
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#5159a2] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    2
                  </div>
                  <h3 className="font-bold mb-2">Upload Video</h3>
                  <p className="text-sm text-gray-600">
                    Drag and drop your .mp4, .mov, or .360 file. Works on desktop and mobile
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#5159a2] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    3
                  </div>
                  <h3 className="font-bold mb-2">Select Your Lap</h3>
                  <p className="text-sm text-gray-600">
                    We detect all laps automatically. Choose your fastest or cleanest lap
                  </p>
                </div>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-[#5159a2] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  4
                </div>
                <h3 className="font-bold mb-2">View in 3D</h3>
                <p className="text-sm text-gray-600">
                  Your racing line appears in stunning 3D. Share, compare, and improve!
                </p>
              </div>
            </div>

            {/* CTA after How It Works */}
            <div className="text-center mt-12">
              <button
                onClick={openImportModal}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#5159a2] text-white rounded-xl font-bold hover:bg-[#424d8a] transition-all shadow-lg hover:shadow-xl cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Start Importing Now
              </button>
              <p className="text-sm text-gray-500 mt-3">Paddock Pass required from your first import</p>
            </div>
          </div>
        </section>


        {/* FAQ Section */}
        <section className="row-full py-16 sm:py-24 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-3xl sm:text-4xl font-racing uppercase text-center mb-12">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-bold cursor-pointer">Which GoPro models are supported?</summary>
                <p className="mt-3 text-gray-600">
                  All GoPro cameras from Hero 5 onwards include GPS. This includes Hero 5, 6, 7, 8, 9, 10, 11, 12, and the GoPro Max 360°.
                </p>
              </details>

              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-bold cursor-pointer">Can I use this on mobile?</summary>
                <p className="mt-3 text-gray-600">
                  Yes! However, due to browser memory limitations, mobile devices can only process videos up to 800MB.
                  For larger files, use a desktop computer or trim your video first using the GoPro Quik app.
                </p>
              </details>

              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-bold cursor-pointer">Is my video uploaded to your servers?</summary>
                <p className="mt-3 text-gray-600">
                  No! The video is processed entirely in your browser. Only the extracted GPS coordinates are uploaded to create your racing line.
                  Your video footage remains completely private.
                </p>
              </details>

              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-bold cursor-pointer">What if GPS was disabled on my camera?</summary>
                <p className="mt-3 text-gray-600">
                  You'll need to enable GPS in your GoPro settings before recording. If your existing video has no GPS data,
                  we won't be able to extract telemetry. Make sure GPS is enabled for future sessions.
                </p>
              </details>

              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-bold cursor-pointer">Do I need a Paddock Pass subscription?</summary>
                <p className="mt-3 text-gray-600">
                  Yes. Paddock Pass is required from your first GoPro import. Subscription checkout is handled securely by Stripe.
                </p>
              </details>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="row-full py-16 sm:py-24 bg-[#5159a2] text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-racing uppercase mb-6">
              Ready To Analyze Your Laps?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Import your first GoPro video and see your racing line in 3D
            </p>
            <button
              onClick={openImportModal}
              className="inline-block px-8 py-4 bg-white text-[#5159a2] rounded-xl font-bold hover:shadow-xl transition-shadow cursor-pointer"
            >
              Subscribe to import
            </button>
            <p className="text-sm opacity-80 mt-3">Paddock Pass required</p>
          </div>
        </section>
      </div>

      {/* Notifications */}
      <div className="fixed left-1/2 transform -translate-x-1/2 top-32 w-full max-w-sm space-y-2 z-[9999]">
        {importError && (
          <Notification
            message={importError}
            type="error"
            onClose={() => setImportError(null)}
          />
        )}
        {importWarning && (
          <Notification
            message={importWarning}
            type="warning"
            onClose={() => setImportWarning(null)}
          />
        )}
      </div>
    </Layout>
  )
}

export default GoProImporterPage
